<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#image-upload-main" class="visually-hidden skip-link">Skip to main content</a>

  <div class="modal-overlay" @click.self="$emit('close')" role="dialog" aria-modal="true" :aria-labelledby="title + '-heading'">
    <div class="modal-content">
      <header class="modal-header">
        <h1 :id="title + '-heading'" class="modal-title">{{ title }}</h1>
        <button @click="$emit('close')" class="close-btn" aria-label="Close image upload modal">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </header>

      <main id="image-upload-main" class="modal-body">
        <!-- File Upload Section -->
        <section v-if="!selectedImage" class="upload-section" role="region" aria-labelledby="upload-heading">
          <h2 id="upload-heading" class="visually-hidden">Image Upload</h2>
          <!-- Hidden file input - MUST be outside button for browser compatibility -->
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleFileSelect"
            class="hidden-file-input"
            aria-label="Choose image file"
          />
          <div
            :class="['upload-area', { 'drag-over': isDragOver }]"
            @click="triggerFileInput"
            @drop="handleDropWithState"
            @dragover.prevent="isDragOver = true"
            @dragenter.prevent="isDragOver = true"
            @dragleave="isDragOver = false"
            role="button"
            tabindex="0"
            @keydown.enter="triggerFileInput"
            @keydown.space.prevent="triggerFileInput"
            aria-label="Click to upload image or drag and drop image files here. Supported formats: JPG, PNG, WEBP up to 5MB"
            aria-describedby="upload-instructions"
          >
            <div class="upload-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#C1C1C1" stroke-width="2" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="12" y1="17" x2="12" y2="9"/>
              </svg>
            </div>
            <p class="upload-text">Click to upload or drag and drop</p>
            <p id="upload-instructions" class="upload-hint">JPG, PNG, WEBP up to 5MB</p>
          </div>
        </section>

        <!-- Image Editor Section -->
        <section v-else class="editor-section" role="region" aria-labelledby="editor-heading">
          <h2 id="editor-heading" class="visually-hidden">Image Editor</h2>

          <div class="image-preview-container">
            <div class="image-preview-full" :style="previewContainerStyle" @wheel="handleWheel" role="img" :aria-label="`Image preview with ${cropShape} crop overlay. Use mouse wheel to zoom, drag to reposition.`">
              <div
                v-if="selectedImage"
                :style="imageStyle"
                @mousedown="startDrag"
                @wheel="handleWheel"
                role="img"
                aria-label="Selected image for editing"
              ></div>
              <img
                :src="selectedImage"
                @load="onImageLoad"
                style="display: none;"
                alt="Hidden image for loading"
              />
              <!-- Grid Overlay -->
              <div v-if="showGrid" class="grid-overlay" aria-hidden="true">
                <div class="grid-line grid-vertical" style="left: 33.33%"></div>
                <div class="grid-line grid-vertical" style="left: 66.67%"></div>
                <div class="grid-line grid-horizontal" style="top: 33.33%"></div>
                <div class="grid-line grid-horizontal" style="top: 66.67%"></div>
              </div>
              
              <!-- Dynamic Crop Overlay: Only show for non-profile images -->
              <div v-if="imageType !== 'profile' && ['circle', 'rectangle'].includes(cropShape)"
                   :class="getCropOverlayClass()" 
                   :style="getCropOverlayStyle()" 
                   aria-hidden="true">
              </div>
            </div>
          </div>

          <!-- Drag hint right below image -->
          <p class="drag-hint">Drag image to reposition</p>

          <div class="editor-controls">
            <!-- Fit to Image Window Control - Available for all image types -->
            <div class="control-group">
              <label class="control-label">
                <input
                  type="checkbox"
                  v-model="fitToWindow"
                  class="fit-checkbox"
                  id="fit-to-window"
                />
                <span class="checkbox-label">Fit to image window</span>
              </label>
            </div>

            <!-- Zoom Control -->
            <div class="control-group">
              <label for="zoom-slider" class="control-label">Zoom</label>
              <div class="zoom-controls">
                <div class="slider-container">
                  <input
                    id="zoom-slider"
                    type="range"
                    :min="minZoom"
                    max="3"
                    step="0.1"
                    v-model="zoom"
                    class="zoom-slider"
                    :aria-label="`Zoom level: ${Math.round(zoom * 100)}%`"
                    aria-describedby="zoom-value"
                  />
                  <span id="zoom-value" class="zoom-value">{{ Math.round(zoom * 100) }}%</span>
                </div>
              </div>
            </div>

          </div>
        </section>
      </main>

      <footer class="modal-actions">
        <button @click="$emit('close')" class="action-button cancel" aria-label="Cancel and close modal">Cancel</button>
        <button @click="triggerFileInput" v-if="!selectedImage" class="action-button secondary" aria-label="Upload a new image">Upload Image</button>
        <button @click="changeImage" v-if="selectedImage" class="action-button secondary" aria-label="Choose a different image">Change Image</button>
        <button
          @click="saveImage"
          :disabled="isUploading || !hasChanges"
          :class="['action-button', hasChanges ? 'primary-active' : 'primary']"
          :aria-label="isUploading ? 'Uploading image...' : 'Save the edited image'"
        >
          {{ isUploading ? 'Uploading...' : 'Save' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { uploadPortfolioImage, uploadProfileImage, uploadBackgroundImage } from '../firebaseStorage'
import { optimizeImage } from '../utils/imageOptimization'
import { auth } from '../firebase'
import { storage } from '../firebase'
import { ref as storageRef, deleteObject } from 'firebase/storage'

export default {
  name: 'ImageUploadModal',
  props: {
    title: {
      type: String,
      default: 'Upload Image'
    },
    currentImage: {
      type: String,
      default: ''
    },
    savedZoom: {
      type: Number,
      default: 1.0
    },
    savedPositionX: {
      type: Number,
      default: 50
    },
    savedPositionY: {
      type: Number,
      default: 50
    },
    overlayShape: {
      type: String,
      default: 'circle', // 'circle' for profile, 'rectangle' for background
      validator: (value) => ['circle', 'rectangle'].includes(value)
    },
    imageType: {
      type: String,
      default: 'profile', // 'profile', 'background', 'portfolio'
      validator: (value) => ['profile', 'background', 'portfolio'].includes(value)
    },
    savedCropShape: {
      type: String,
      default: ''
    },
    savedAspectRatio: {
      type: String,
      default: 'free'
    },
    savedCornerRadius: {
      type: Number,
      default: 16
    },
    accentColor: {
      type: String,
      default: '#6566be', // Default to science blue, can be 'artist', 'scientist', or hex color
      validator: (value) => ['artist', 'scientist', 'both'].includes(value) || value.startsWith('#')
    },
  },
  emits: ['close', 'upload'],
  setup(props, { emit }) {
    const fileInput = ref(null)
    const selectedImage = ref(props.currentImage || '')
    const selectedFile = ref(null) // Store the actual file for Cloudinary upload
    const isUploading = ref(false) // Track upload state
    const zoom = ref(parseFloat(props.savedZoom) || 1.0)
    const positionX = ref(parseFloat(props.savedPositionX) || 50)
    const positionY = ref(parseFloat(props.savedPositionY) || 50)
    const isDragging = ref(false)
    const dragStart = ref({ x: 0, y: 0 })
    const originalSize = ref({ width: 0, height: 0 })
    const showGrid = ref(false)
    const isDragOver = ref(false) // Track file drag over upload area


    const isImageFillingWindow = () => {
      if (!originalSize.value.width || !originalSize.value.height) return false

      const dimensions = getCropOverlayDimensions()
      const containerWidth = dimensions.width
      const containerHeight = dimensions.height

      // Calculate expected FIT zoom (entire image visible)
      const imageAspect = originalSize.value.width / originalSize.value.height
      const containerAspect = containerWidth / containerHeight

      let expectedFitZoom
      if (imageAspect < containerAspect) {
        // Portrait image - needs zoom out to fit height
        expectedFitZoom = (containerHeight * imageAspect) / containerWidth
      } else {
        // Landscape or same aspect - fits at 100%
        expectedFitZoom = 1.0
      }

      // Check if current zoom matches fit zoom and image is centered
      const zoomMatch = Math.abs(zoom.value - expectedFitZoom) < 0.05
      const positionCentered = Math.abs(positionX.value - 50) < 5 && Math.abs(positionY.value - 50) < 5

      return zoomMatch && positionCentered
    }
    
    // Fit to window checkbox - starts unchecked by default
    const fitToWindow = ref(false)

    // Computed: Check if there are unsaved changes
    const hasChanges = computed(() => {
      // New file selected = definitely has changes
      if (selectedFile.value) return true

      // No image at all = nothing to save
      if (!selectedImage.value) return false

      // Existing image with modified zoom/position
      const zoomChanged = Math.abs(zoom.value - props.savedZoom) > 0.01
      const posXChanged = Math.abs(positionX.value - props.savedPositionX) > 0.5
      const posYChanged = Math.abs(positionY.value - props.savedPositionY) > 0.5

      return zoomChanged || posXChanged || posYChanged
    })

    // Computed: Get the actual color based on accentColor prop
    const activeColor = computed(() => {
      switch (props.accentColor) {
        case 'artist':
          return '#f8b19b'
        case 'scientist':
          return '#6566be'
        case 'both':
          return '#d1d2e6'
        default:
          return props.accentColor // Assume it's a hex color
      }
    })

    // Simplified: Always circle for profile, rectangle for others
    const cropShape = ref(
      props.imageType === 'profile' ? 'circle' : 'rectangle'
    )
    
    // Fixed aspect ratio - crop overlay doesn't change based on fit to window
    const aspectRatio = computed(() => {
      // Crop overlay stays consistent - fit to window only affects zoom
      return props.imageType === 'profile' ? 'square' : 'landscape'
    })
    const cornerRadius = ref(props.savedCornerRadius || 16)
    
    // Aspect ratio presets
    const ASPECT_RATIOS = {
      free: { ratio: null, label: 'Free Form (Full Image)' },
      square: { ratio: 1, label: 'Square (1:1)' },
      portrait: { ratio: 4/5, label: 'Portrait (4:5)' },
      landscape: { ratio: 16/9, label: 'Landscape (16:9)' },
      golden: { ratio: 1.618, label: 'Golden Ratio' },
      instagram: { ratio: 1, label: 'Instagram (1:1)' },
      story: { ratio: 9/16, label: 'Story (9:16)' }
    }
    
    // CONDITIONAL: Circle for profile images only, rectangle for others
    const getCropShapesForImageType = () => {
      if (props.imageType === 'profile') {
        return {
          circle: { label: 'Circle', icon: '⭕', category: 'basic' }
        }
      } else {
        return {
          rectangle: { label: 'Rectangle', icon: '▭', category: 'basic' }
        }
      }
    }
    
    const CROP_SHAPES = getCropShapesForImageType()
    

    const minZoom = computed(() => {
      // Calculate minimum zoom needed to fit entire image in container
      if (originalSize.value.width && originalSize.value.height) {
        const dimensions = getCropOverlayDimensions()
        const containerWidth = dimensions.width
        const containerHeight = dimensions.height

        // For tall/portrait images, we need to zoom out more
        const imageAspect = originalSize.value.width / originalSize.value.height
        const containerAspect = containerWidth / containerHeight

        if (imageAspect < containerAspect) {
          // Image is taller than container aspect - need to zoom out to fit height
          // At 100% zoom, image width = container width, so height = containerWidth / imageAspect
          // To fit: we need height <= containerHeight
          // So zoom = containerHeight / (containerWidth / imageAspect) = containerHeight * imageAspect / containerWidth
          const fitZoom = (containerHeight * imageAspect) / containerWidth
          return Math.min(0.05, fitZoom * 0.5) // Allow 50% smaller than fit
        }
      }
      return 0.05 // Allow zooming out to 5% minimum
    })
    const maxZoom = 3.0 // 300% maximum
    

    // File size limit: 5MB
    const MAX_FILE_SIZE = 5 * 1024 * 1024



    const imageStyle = computed(() => {
      if (!selectedImage.value) {
        return { display: 'none' }
      }
      
      const dimensions = getCropOverlayDimensions()
      
      return {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        backgroundColor: '#ffffff', // White background for transparent PNGs
        backgroundImage: `url(${selectedImage.value})`,
        backgroundSize: `${zoom.value * 100}%`,
        backgroundPosition: `${positionX.value}% ${positionY.value}%`,
        backgroundRepeat: 'no-repeat',
        userSelect: 'none',
        transition: 'none'
      }
    })

    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click()
      }
    }

    const validateFile = (file) => {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.')
        return false
      }
      
      if (file.size > MAX_FILE_SIZE) {
        alert('File size must be less than 5MB.')
        return false
      }

      return true
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file && validateFile(file)) {
        selectedFile.value = file
        const reader = new FileReader()
        reader.onload = (e) => {
          selectedImage.value = e.target.result
          resetControls()
        }
        reader.readAsDataURL(file)
      }
    }

    const handleDrop = (event) => {
      event.preventDefault()
      const file = event.dataTransfer.files[0]
      if (file && validateFile(file)) {
        selectedFile.value = file // Store the file for Cloudinary upload
        const reader = new FileReader()
        reader.onload = (e) => {
          selectedImage.value = e.target.result
          resetControls()
        }
        reader.readAsDataURL(file)
      }
    }

    // Wrapper that resets drag state after drop
    const handleDropWithState = (event) => {
      isDragOver.value = false
      handleDrop(event)
    }

    const onImageLoad = (event) => {
      originalSize.value = {
        width: event.target.naturalWidth,
        height: event.target.naturalHeight
      }
      
      // Set reasonable defaults for new images
      if (!props.currentImage) {
        positionX.value = 50
        positionY.value = 50
        
        // Calculate a reasonable default zoom to make image visible
        const dimensions = getCropOverlayDimensions()
        const containerWidth = dimensions.width
        const containerHeight = dimensions.height
        
        // Calculate zoom to fit image in container (like object-fit: contain)
        const zoomToFitWidth = containerWidth / originalSize.value.width
        const zoomToFitHeight = containerHeight / originalSize.value.height
        const defaultZoom = Math.min(zoomToFitWidth, zoomToFitHeight)
        
        // Set initial zoom based on fitToWindow setting
        if (fitToWindow.value) {
          calculateFitToWindowZoom()
        } else {
          zoom.value = Math.max(0.1, Math.min(3.0, defaultZoom)) // Reasonable default zoom
        }
      }
    }

    const startDrag = (event) => {
      if (!selectedImage.value) return
      
      event.preventDefault()
      isDragging.value = true
      dragStart.value = {
        x: event.clientX,
        y: event.clientY,
        startPosX: positionX.value,
        startPosY: positionY.value
      }
    }

    
    const handleWheel = (event) => {
      event.preventDefault()
      const delta = event.deltaY > 0 ? -0.1 : 0.1
      const currentZoom = zoom.value || 1.0
      const minZoomValue = minZoom.value || 0.1
      const maxZoomValue = maxZoom || 3.0
      
      const newZoom = Math.max(minZoomValue, Math.min(maxZoomValue, currentZoom + delta))
      
      // Ensure the new zoom is a valid number
      if (!isNaN(newZoom) && isFinite(newZoom)) {
        zoom.value = newZoom
      }
    }
    
    const onMouseMove = (event) => {
      if (!isDragging.value) return
      
      // Calculate mouse movement in pixels
      const deltaX = event.clientX - dragStart.value.x
      const deltaY = event.clientY - dragStart.value.y
      

      const sensitivity = 0.5 // Much faster/more responsive
      
      // For background-image: drag RIGHT = move image RIGHT = INCREASE backgroundPosition
      const percentDeltaX = deltaX * sensitivity
      // For background-image: drag UP = move image UP = DECREASE backgroundPosition (Y axis is inverted)
      const percentDeltaY = -deltaY * sensitivity
      
      // Apply movement with proper direction and bounds
      positionX.value = Math.max(0, Math.min(100, dragStart.value.startPosX + percentDeltaX))
      positionY.value = Math.max(0, Math.min(100, dragStart.value.startPosY + percentDeltaY))
    }

    const onMouseUp = () => {
      isDragging.value = false
    }



    const calculateFitToWindowZoom = () => {
      if (!originalSize.value.width || !originalSize.value.height) return

      const dimensions = getCropOverlayDimensions()
      const containerWidth = dimensions.width
      const containerHeight = dimensions.height

      // With background-size: X%, the image width = X% of container width
      // At 100% zoom (background-size: 100%), image width = container width
      // Image height at 100% = containerWidth * (originalHeight / originalWidth)

      const imageAspect = originalSize.value.width / originalSize.value.height
      const containerAspect = containerWidth / containerHeight

      let fitZoom

      if (imageAspect < containerAspect) {
        // Image is taller (portrait) - at 100% zoom, height exceeds container
        // Height at 100% = containerWidth / imageAspect
        // To fit: containerWidth / imageAspect * zoom = containerHeight
        // zoom = containerHeight * imageAspect / containerWidth
        fitZoom = (containerHeight * imageAspect) / containerWidth
      } else {
        // Image is wider (landscape) or same aspect - fits at 100% zoom
        fitZoom = 1.0
      }

      // Ensure zoom is within valid range
      const minZoomValue = minZoom.value || 0.05
      const maxZoomValue = maxZoom || 3.0
      zoom.value = Math.max(minZoomValue, Math.min(maxZoomValue, fitZoom))
    }
    
    const resetControls = () => {
      positionX.value = 50
      positionY.value = 50
      
      // Set zoom based on fitToWindow setting
      if (fitToWindow.value) {
        calculateFitToWindowZoom()
      } else {
        zoom.value = 1.0 // Reset to 100% zoom
      }
      
      // Reset image dimensions
      originalSize.value = { width: 0, height: 0 }
    }

    // Clean up old Firebase Storage image
    const cleanupOldImage = async (imageUrl) => {
      try {
        // Extract the file path from Firebase Storage URL
        if (imageUrl && imageUrl.includes('firebasestorage.googleapis.com')) {
          // Parse the URL to get the file path
          const urlParts = imageUrl.split('/o/')[1]
          if (urlParts) {
            const filePath = decodeURIComponent(urlParts.split('?')[0])
            const oldImageRef = storageRef(storage, filePath)
            await deleteObject(oldImageRef)
          }
        }
      } catch (error) {
        // Cleanup failure shouldn't stop upload
      }
    }


    // CONDITIONAL: Return appropriate overlay class based on crop shape
    const getCropOverlayClass = () => {
      const baseClass = 'crop-overlay'
      switch (cropShape.value) {
        case 'circle':
          return `${baseClass} circular-overlay` 
        case 'rectangle':
          return `${baseClass} rectangular-overlay` 
        default:
          return `${baseClass} circular-overlay` 
      }
    }


    const getCropOverlayStyle = () => {
      // CRITICAL: Use exact same dimensions as final display for each image type
      const dimensions = getCropOverlayDimensions()
      
      return {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`
      }
    }



    const previewContainerStyle = computed(() => {
      const styles = {
        position: 'relative',
        border: '2px solid #E0E0E0',
        overflow: 'hidden',
        background: '#ffffff'
      }

      // CRITICAL: Use exact same dimensions as final display for each image type
      if (props.imageType === 'profile') {
        // Profile: 300x300px circle (matches ProfilePageNew.vue)
        styles.width = '300px'
        styles.height = '300px'
        styles.borderRadius = '50%' // Circular container
      } else if (props.imageType === 'background') {
        // Background: 350x200px rectangle (matches profile edit pages)
        styles.width = '350px'
        styles.height = '200px'
        styles.borderRadius = '8px'  // Rectangular container
      } else {
        // Portfolio: 300x300px rectangle (standard)
        styles.width = '300px'
        styles.height = '300px'
        styles.borderRadius = '8px'  // Rectangular container
      }

      return styles
    })


    const changeImage = () => {
      selectedImage.value = ''
      triggerFileInput()
    }





    const getCropOverlayDimensions = () => {
      // CRITICAL: Use exact same dimensions as final display for each image type
      if (props.imageType === 'profile') {
        return { width: 300, height: 300 } // Matches ProfilePageNew.vue
      } else if (props.imageType === 'background') {
        return { width: 350, height: 200 } // Matches profile edit pages background preview
      } else {
        return { width: 300, height: 300 } // Portfolio standard
      }
    }

    const saveImage = async () => {
      if (isUploading.value) {
        return
      }

      try {
        isUploading.value = true

        // If there's a new file, upload it to Firebase Storage
        if (selectedFile.value) {
          // Get current user ID
          const currentUser = auth.currentUser
          if (!currentUser) {
            throw new Error('User not authenticated')
          }

          // Determine upload function based on title (portfolio is default)
          let uploadFunction = uploadPortfolioImage
          if (props.title.toLowerCase().includes('profile')) {
            uploadFunction = uploadProfileImage
          } else if (props.title.toLowerCase().includes('background')) {
            uploadFunction = uploadBackgroundImage
          }

          // Clean up old image before uploading new one
          if (props.currentImage) {
            await cleanupOldImage(props.currentImage)
          }

          let fileToUpload = selectedFile.value

          // Optimize image before upload
          const optimizationOptions = {
            maxWidth: props.title.toLowerCase().includes('profile') ? 800 : 1200,
            maxHeight: props.title.toLowerCase().includes('profile') ? 800 : 1200,
            quality: 0.9,
            format: 'jpeg'
          }
          
          fileToUpload = await optimizeImage(selectedFile.value, optimizationOptions)

          // Upload processed file to Firebase Storage
          const uploadResult = await uploadFunction(fileToUpload, currentUser.uid)

          // Emit the new Firebase Storage URL with complete customization data
          const cropDimensions = getCropOverlayDimensions()
          const emitData = {
            imageUrl: uploadResult.secure_url,
            zoom: zoom.value,
            positionX: positionX.value,
            positionY: positionY.value,
            cropShape: cropShape.value,
            aspectRatio: aspectRatio.value,
            cornerRadius: cornerRadius.value,
            cropWidth: cropDimensions.width,
            cropHeight: cropDimensions.height,
            fitToWindow: fitToWindow.value
          }
          emit('upload', emitData)
        } else if (props.currentImage) {
          // No new file, but we have an existing image - just save the zoom/position changes
          const cropDimensions = getCropOverlayDimensions()
          const emitData = {
            imageUrl: props.currentImage,
            zoom: zoom.value,
            positionX: positionX.value,
            positionY: positionY.value,
            cropShape: cropShape.value,
            aspectRatio: aspectRatio.value,
            cornerRadius: cornerRadius.value,
            cropWidth: cropDimensions.width,
            cropHeight: cropDimensions.height,
            fitToWindow: fitToWindow.value
          }
          emit('upload', emitData)
        } else {
          return
        }
      } catch (error) {
        // Show more specific error message
        let errorMessage = 'Failed to upload image. '
        if (error.message === 'User not authenticated') {
          errorMessage += 'Please log in and try again.'
        } else if (error.code === 'storage/unauthorized') {
          errorMessage += 'Permission denied. Please check your account.'
        } else if (error.code === 'storage/canceled') {
          errorMessage += 'Upload was canceled.'
        } else if (error.code === 'storage/unknown') {
          errorMessage += 'Unknown error occurred. Please try again.'
        } else {
          errorMessage += error.message || 'Please try again.'
        }
        alert(errorMessage)
      } finally {
        isUploading.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    })


    // Watch for fitToWindow changes and adjust zoom accordingly
    watch(fitToWindow, (newValue) => {
      if (newValue && originalSize.value.width && originalSize.value.height) {
        calculateFitToWindowZoom()
        positionX.value = 50
        positionY.value = 50
      }
    })
    
    // Watch zoom and position changes to update checkbox state
    watch([zoom, positionX, positionY], () => {
      if (originalSize.value.width && originalSize.value.height) {
        const shouldBeChecked = isImageFillingWindow()
        if (fitToWindow.value !== shouldBeChecked) {
          fitToWindow.value = shouldBeChecked
        }
      }
    })

    onUnmounted(() => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    })

    return {
      fileInput,
      selectedImage,
      selectedFile,
      isUploading,
      zoom,
      positionX,
      positionY,
      imageStyle,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      handleDropWithState,
      isDragOver,
      onImageLoad,
      startDrag,
      changeImage,
      saveImage,
      minZoom,
      handleWheel,
      showGrid,
      aspectRatio,
      cropShape,
      cornerRadius,
      fitToWindow,
      hasChanges,
      activeColor,
      ASPECT_RATIOS,
      CROP_SHAPES,
      getCropOverlayClass,
      getCropOverlayStyle,
      previewContainerStyle,
      getCropOverlayDimensions,
      calculateFitToWindowZoom,
      isImageFillingWindow
    }
  }
}
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #333333;
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

/* Enhanced focus indicators for keyboard users */
.close-btn:focus:not(:focus-visible),
.action-button:focus:not(:focus-visible),
.reset-button:focus:not(:focus-visible),
.upload-area:focus:not(:focus-visible) {
  outline: none;
}

.close-btn:focus-visible,
.action-button:focus-visible,
.reset-button:focus-visible,
.upload-area:focus-visible {
  outline: 2px solid #333333;
  outline-offset: 2px;
}

.zoom-slider:focus-visible {
  outline: 2px solid #333333;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .action-button,
  .reset-button,
  .upload-area {
    border-width: 3px;
  }

  .close-btn {
    border: 2px solid currentColor;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .action-button,
  .reset-button,
  .close-btn,
  .upload-area {
    transition: none;
  }

  .skip-link {
    transition: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .action-button,
  .reset-button {
    min-height: 44px;
    min-width: 44px;
    padding: 12px 24px;
  }

  .close-btn {
    min-height: 44px;
    min-width: 44px;
    padding: 12px;
  }

  .upload-area {
    min-height: 44px;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: var(--stdRadius);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  position: relative;
  z-index: 1;
}

.modal-header h3 {
  color: var(--headersColor);
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: var(--textColor);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 1rem;
  position: relative;
  z-index: 1;
}

.upload-section {
  text-align: center;
  position: relative;
}

/* Hidden file input - positioned off-screen for accessibility */
.hidden-file-input {
  position: absolute;
  left: -9999px;
  opacity: 0;
  width: 1px;
  height: 1px;
}

.upload-area {
  border: 2px dashed #C1C1C1;
  border-radius: var(--stdRadius);
  padding: 3rem 2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f9f9f9;
}

.upload-area:hover {
  border-color: #999;
  background: #e8e8e8;
}

.upload-area.drag-over {
  border-color: #666;
  background: #ddd;
  border-style: solid;
}

.upload-icon {
  margin-bottom: 1rem;
}

.upload-text {
  font-size: 1.1rem;
  color: var(--textColor);
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.upload-hint {
  font-size: 0.9rem;
  color: var(--textColor);
  opacity: 0.7;
  margin: 0;
}

.editor-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.image-preview-container {
  display: flex;
  justify-content: center;
}

/* Drag hint below image */
.drag-hint {
  text-align: center;
  color: #888;
  font-size: 0.85rem;
  font-style: italic;
  margin: 0.75rem 0 0 0;
}

/* Dynamic styles applied via previewContainerStyle computed property */

.image-preview-full {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview-full img {

  max-width: none;
  max-height: none;
  object-fit: none;
  cursor: move;
  /* Size and position controlled entirely by JavaScript transforms */
}

/* ===== GRID OVERLAY ===== */
.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
}

.grid-line {
  position: absolute;
  background: rgba(255, 255, 255, 0.9);
  pointer-events: none;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.grid-vertical {
  width: 2px;
  height: 100%;
  top: 0;
}

.grid-horizontal {
  height: 2px;
  width: 100%;
  left: 0;
}

/* ===== CROP OVERLAYS ===== */
.crop-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
  z-index: 3;
}


.circular-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 3;
  width: 300px; /* Default fallback */
  height: 300px; /* Default fallback */
  border: 3px solid #666666;
  border-radius: 50%;
}

.rectangular-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 3;
  border: 3px solid #666666;
  border-radius: 8px;
  /* Width and height set dynamically via inline styles */
}

/* Removed corner indicators - they were buggy and not supposed to be there */



.editor-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-group label {
  font-weight: 600;
  color: var(--textColor);
  font-size: 0.9rem;
}

/* Checkbox styling */
.control-group label:has(.fit-checkbox) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  cursor: pointer;
}

.fit-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #666666;
  cursor: pointer;
}

.checkbox-label {
  font-size: 0.9rem;
  color: var(--textColor);
}

.control-hint {
  font-size: 0.8rem;
  color: var(--textColor);
  opacity: 0.7;
  margin: 0.25rem 0 0 0;
  line-height: 1.3;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.zoom-slider {
  flex: 1;
  height: 6px;
  background: #C1C1C1;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.zoom-slider {
  -webkit-appearance: none;
  appearance: none;
}

.zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #666666;
  border-radius: 50%;
  cursor: pointer;
}

.zoom-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #666666;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

.zoom-value {
  font-size: 0.9rem;
  color: var(--textColor);
  min-width: 50px;
  text-align: right;
}

.control-button {
  background: none;
  border: 1px solid #C1C1C1;
  color: var(--textColor);
  padding: 0.5rem 1rem;
  border-radius: calc(var(--stdRadius) / 2);
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  margin-right: 0.5rem;
}

.control-button:hover {
  background: var(--neutralColor);
  border-color: #FCFCFE;
}



/* ===== SHAPE SELECTOR ===== */
.shape-selector {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.shape-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  background: white;
  border: 2px solid #E0E0E0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;
  font-size: 0.8rem;
}

.shape-btn:hover {
  border-color: #666666;
  background: #f5f5f5;
}

.shape-btn.active {
  border-color: #666666;
  background: #666666;
  color: white;
}

.shape-icon {
  font-size: 1.2rem;
  margin-bottom: 0.25rem;
}

.shape-label {
  font-size: 0.75rem;
  font-weight: 500;
}

/* ===== ASPECT RATIO SELECT ===== */
.aspect-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #C1C1C1;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  cursor: pointer;
}

.aspect-select:focus {
  outline: 2px solid #666666;
  outline-offset: 2px;
  border-color: #666666;
}

/* ===== ZOOM CONTROLS ===== */
.zoom-controls {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}





.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 0 1.5rem 1.5rem;
  position: relative;
  z-index: 1;
}

.action-button {
  padding: 0.75rem 1.5rem;
  border-radius: calc(var(--stdRadius) / 2);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
}

.action-button.cancel {
  background: white;
  border-color: #C1C1C1;
  color: #333;
}

.action-button.cancel:hover {
  background: #f5f5f5;
  border-color: #999;
  color: #333;
}

.action-button.secondary {
  background: white;
  border-color: #C1C1C1;
  color: #333333;
  font-weight: 600;
}

.action-button.secondary:hover {
  background: #999;
  border-color: #999;
  color: white;
}

.action-button.primary {
  background: #e8e8e8;
  border-color: #ccc;
  color: #888;
  font-weight: 600;
}

.action-button.primary:hover:not(:disabled) {
  background: #ddd;
  border-color: #bbb;
  color: #666;
}

.action-button.primary-active {
  background: #666 !important;
  border-color: #666 !important;
  color: white;
  font-weight: 600;
}

.action-button.primary-active:hover:not(:disabled) {
  background: #555 !important;
  border-color: #555 !important;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-button.primary:disabled,
.action-button.primary-active:disabled {
  background: #e8e8e8 !important;
  border-color: #ddd !important;
  color: #aaa !important;
}

.action-button:disabled:hover {
  background: #e8e8e8 !important;
  border-color: #ddd !important;
  color: #aaa !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }
  
  .upload-area {
    padding: 2rem 1rem;
  }
  
  .editor-section {
    gap: 1.5rem;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .action-button {
    width: 100%;
  }
}

/* ===== BASIC POSITION HINT ===== */
.basic-position-hint {
  text-align: center;
  padding: 0.75rem;
  background: rgba(102, 102, 102, 0.1);
  border: 1px dashed #666666;
  border-radius: 6px;
  color: #666666;
  font-size: 0.85rem;
  font-style: italic;
}

/* ===== SIZE INDICATOR ===== */
.size-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(101, 103, 190, 0.1);
  border: 1px solid rgba(101, 103, 190, 0.2);
  border-radius: 6px;
  margin-top: 1rem;
}

.size-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6567BE;
}

.size-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
  font-family: 'Courier New', monospace;
}
</style>